import { ref } from "vue";
import { GamesCardGame } from "../models/game-card-game";
import { useCurrentBreakpoint } from "@sazka/utils/composables/use-breakpoints";
import { GamesFilterParameters, GamesFilterSortType } from "../models/games-filter-parameters";
import { httpClient } from "@sazka/utils/http-clients/http-client";
import { GamesFilterPage } from "../models/games-filter-page";
import { GamesFilterOptions } from "../models/games-filter-options";
import { UrlUtils } from "@sazka/utils/url";

const getPageSize = () => {
    switch (useCurrentBreakpoint(["sm", "sm2", "md", "lg", "xl"]).value) {
        case "sm":
            return 18;
        case "sm2":
            return 24;
        case "md":
        case "lg":
            return 30;
        case "xl":
            return 36;
        default:
            return 12;
    }
};

const loadedGames = ref<GamesCardGame[]>([]);
const totalCount = ref<number>();
const isNextPageAvailable = ref(true);
const areGamesLoading = ref(true);
const filterParameters = ref<GamesFilterParameters>({
    genres: [],
    manufacturers: [],
    reels: [],
    lines: [],
    sortType: GamesFilterSortType.Default,
    pageSize: getPageSize(),
    page: 1,
    getCountOnly: false,
});
const filterOptions = ref<GamesFilterOptions>({
    genres: [],
    manufacturers: [],
    reels: [],
    lines: [],
});

export function useGamesGrid() {
    const loadGamePage = async () => {
        const gamesPage = await httpClient.post<GamesFilterPage>("api/games/filter", filterParameters.value);

        if (filterParameters.value.page === 1) {
            loadedGames.value = gamesPage.data.games;
        } else {
            loadedGames.value = loadedGames.value.concat(gamesPage.data.games);
        }

        totalCount.value = gamesPage.data.totalCount;
        isNextPageAvailable.value = gamesPage.data.nextPageAvailable;

        // timeout to wait for observer to process skeletons movement
        setTimeout(() => {
            areGamesLoading.value = false;
        }, 100);
    };

    const scrollToGrid = () => {
        const grid = document.getElementById("games-grid");
        if (!grid) return;

        grid.scrollIntoView({ behavior: "smooth" });
    };

    const init = async () => {
        const options = await httpClient.get<GamesFilterOptions>("api/games/filter/categories");
        filterOptions.value = options.data;

        const manufacturersFromUrl = UrlUtils.getQueryString("filter");
        if (manufacturersFromUrl) {
            const parsedManufacturers = manufacturersFromUrl.split(";").map((m) => m.toLocaleLowerCase());
            filterParameters.value.manufacturers = filterOptions.value.manufacturers
                .filter((m) => parsedManufacturers.includes(m.urlFilterName))
                .map((m) => m.guid);

            if (filterParameters.value.manufacturers.length > 0) {
                scrollToGrid();
                UrlUtils.clearUrlParams(["filter"]);
            }
        }

        await loadGamePage();
    };

    const loadNextPage = async () => {
        areGamesLoading.value = true;
        filterParameters.value.page++;
        await loadGamePage();
    };

    const getFilteredGamesCount = async (filterParameters: GamesFilterParameters) => {
        const gamesPage = await httpClient.post<GamesFilterPage>("api/games/filter", filterParameters);
        return gamesPage.data.totalCount;
    };

    const reloadWithFilter = async () => {
        areGamesLoading.value = true;
        loadedGames.value = [];
        isNextPageAvailable.value = true;
        filterParameters.value.page = 1;
        await loadGamePage();
    };

    const getFilterSortTypeLabel = (type: GamesFilterSortType) => {
        switch (type) {
            case GamesFilterSortType.Newest:
                return "Od nejnovějších";
            case GamesFilterSortType.Oldest:
                return "Od nejstarších";
            case GamesFilterSortType.MostPlayed:
                return "Nejhranější";
        }
    };

    return {
        loadedGames,
        totalCount,
        isNextPageAvailable,
        areGamesLoading,
        filterParameters,
        filterOptions,
        init,
        loadNextPage,
        reloadWithFilter,
        getFilterSortTypeLabel,
        getFilteredGamesCount,
    };
}
